import { GzPolicyUiSettingsGridItemFormWebComponent } from '../../../../../../../../../common/policyPanel/uiSettings/gridTemplate/form';
import { html, TemplateResult } from 'lit';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { RequiredValidator } from '../../../../../../../../../../ui/gzForm/validators/required.validator';
import { CustomizableValidator } from '../../../../../../../../../../ui/gzForm/validators/customizable.validator';
import { MAX_LENGTH, validateExpressionByType } from './utils/validators';
import { customElement, query } from 'lit/decorators.js';
import { GzFormFieldWebComponent } from '../../../../../../../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../../../../../../../ui/gzDropdown';
import { GzInputWebComponent } from '../../../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { FormGroup } from '../../../../../../../../../../ui/gzForm/formGroup/form-group';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL_CONTENT_FILTERING_BY_ITEM } from '../../../../../../../../../../webcomponents.constants';
import { ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType } from '@nirvana/shared/dtos/types/policies/common';
import { getExchangeContentControlExpressionTypeGridOptions } from '../../../../../../../utils/uiSettings/exchange/exchange-protection.values';
import { GzCheckboxWebComponent } from '../../../../../../../../../../ui/gzCheckbox';
import { MaxLengthValidator } from '../../../../../../../../../../ui/gzForm/validators/max-length.validator';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_CONTENT_CONTROL_CONTENT_FILTERING_BY_ITEM)
export class GzExchangeContentControlContentFilteringByItemFormWebComponent extends GzPolicyUiSettingsGridItemFormWebComponent {
  @query('#itemExpression')
  itemExpression: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#itemType')
  itemType: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#itemMatchCase')
  itemMatchCase: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#itemUseProxy')
  itemUseProxy: GzFormFieldWebComponent<GzCheckboxWebComponent>;

  /**
   * Fields from the current component are connected. When an external error is set,
   * any change on another field should remove external validation from server.
   */
  private shouldBeRevalidatedOnFieldChange = false;

  public setError(error: string) {
    this.shouldBeRevalidatedOnFieldChange = true;
    this.itemExpression.setError(error);
    this.itemType.setError(error);
    this.itemMatchCase.setError(error);
    this.itemUseProxy.setError(error);
  }

  renderFormFields(): TemplateResult {
    return html`
      <div class="policy-grid-item-form--field excluded-item-path input--w40">
        <gz-form-field id="itemExpression">
          <gz-input
            gz-form-input
            label="${translateFn('Expression')}"
            placeholder="${translateFn('Type here')}"
            @change="${this.revalidateOnFieldChange}"
          ></gz-input>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field excluded-item-type">
        <gz-form-field id="itemType">
          <gz-single-select
            cannotDelete
            gz-form-input
            label="${translateFn('Type')}"
            .value=${ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType.TEXT}
            .filterOptions=${getExchangeContentControlExpressionTypeGridOptions()}
            @selectionChange="${this.handleTypeFieldChange}"
          ></<gz-single-select>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field">
        <gz-form-field id="itemMatchCase">
          <gz-checkbox
            style="top: 5px"
            gz-form-input
            label="${translateFn('Match case')}"
            @change="${this.revalidateOnFieldChange}"
          ></gz-checkbox>
        </gz-form-field>
      </div>

      <div class="policy-grid-item-form--field">
        <gz-form-field id="itemUseProxy">
          <gz-checkbox
            style="top: 5px"
            gz-form-input
            label="${translateFn('Whole word')}"
            @change="${this.revalidateOnFieldChange}"
          ></gz-checkbox>
        </gz-form-field>
      </div>
    `;
  }

  createFormGroup(): void {
    this.itemType.Validators = [RequiredValidator(translateFn('Required field'))];
    this.itemExpression.Validators = [
      RequiredValidator(translateFn('Required field')),
      MaxLengthValidator(
        MAX_LENGTH,
        translateFn('The expression must have a maximum of {maxLength} characters.', { maxLength: MAX_LENGTH })
      ),
      CustomizableValidator(value =>
        validateExpressionByType(
          this.itemType.value as ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType,
          value
        )
      ),
    ];

    this.formGroup = new FormGroup({
      text: this.itemExpression,
      type: this.itemType,
      matchCase: this.itemMatchCase,
      wholeWord: this.itemUseProxy,
    });
  }

  private revalidateOnFieldChange() {
    if (!this.shouldBeRevalidatedOnFieldChange) {
      return;
    }

    this.shouldBeRevalidatedOnFieldChange = false;
    this.itemExpression.validate();
    this.itemType.validate();
    this.itemMatchCase.validate();
    this.itemUseProxy.validate();
  }

  private handleTypeFieldChange() {
    // skip, because revalidation will be processed in the next step
    if (!this.shouldBeRevalidatedOnFieldChange) {
      this.itemExpression.validate();
      return;
    }

    this.revalidateOnFieldChange();
  }

  resetForm(resetType = true): void {
    if (resetType) {
      this.itemType.value = ExchangeContentControlContentFilteringRuleSettingsFilterByExpressionType.TEXT;
      this.itemType.resetField();
    }

    this.itemExpression.value = '';
    this.itemExpression.resetField();

    this.itemMatchCase.value = false;
    this.itemMatchCase.resetField();

    this.itemUseProxy.value = false;
    this.itemUseProxy.resetField();
  }

  reset() {
    this.resetForm();
  }

  resetOnAdd() {
    this.resetForm(false);
  }
}
